import React, { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useNotificationsStore } from '../../store/notifications.store';

const useStyles = makeStyles({
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: '8px',
    marginRight: '8px',
    borderRadius: '4px',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  icon: {
    fontSize: '18px',
    color: 'rgba(255, 255, 255, 0.8)',
    transition: 'color 0.2s',
  },
  iconActive: {
    color: 'var(--accent-1-color)',
  },
  badge: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    minWidth: '16px',
    height: '16px',
    padding: '0 4px',
    borderRadius: '8px',
    backgroundColor: 'var(--accent-1-color)',
    color: '#fff',
    fontSize: '10px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },
});

const NotificationBell: React.FC = () => {
  const classes = useStyles();
  const [showEmpty, setShowEmpty] = useState(false);
  const emptyRef = useRef<HTMLDivElement>(null);

  const pendingLicensesCount = useNotificationsStore(state => state.pendingLicensesCount);
  const rejectedRequestsCount = useNotificationsStore(state => state.rejectedRequestsCount);
  const pendingRequestsCount = useNotificationsStore(state => state.pendingRequestsCount);
  const expiredLicensesCount = useNotificationsStore(state => state.expiredLicensesCount);
  const pendingReviewsCount = useNotificationsStore(state => state.pendingReviewsCount);
  const reviewResultsCount = useNotificationsStore(state => state.reviewResultsCount);
  const pendingDeliveriesCount = useNotificationsStore(state => state.pendingDeliveriesCount);
  const deliveryRejectionsCount = useNotificationsStore(state => state.deliveryRejectionsCount);
  const accessRequestsCount = useNotificationsStore(state => state.accessRequestsCount);
  const accessRequestResultsCount = useNotificationsStore(state => state.accessRequestResultsCount);
  const ftpAccessRequestsCount = useNotificationsStore(state => state.ftpAccessRequestsCount);
  const ftpAccessResultsCount = useNotificationsStore(state => state.ftpAccessResultsCount);
  const openNotifications = useNotificationsStore(state => state.openNotifications);

  const totalCount = pendingLicensesCount + rejectedRequestsCount + pendingRequestsCount + expiredLicensesCount + pendingReviewsCount + reviewResultsCount + pendingDeliveriesCount + deliveryRejectionsCount + accessRequestsCount + accessRequestResultsCount + ftpAccessRequestsCount + ftpAccessResultsCount;

  // Close empty dropdown on outside click
  useEffect(() => {
    if (!showEmpty) return;
    const handleClick = (e: MouseEvent) => {
      if (emptyRef.current && !emptyRef.current.contains(e.target as Node)) {
        setShowEmpty(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showEmpty]);

  // Auto-hide after 3 seconds
  useEffect(() => {
    if (!showEmpty) return;
    const timer = setTimeout(() => setShowEmpty(false), 3000);
    return () => clearTimeout(timer);
  }, [showEmpty]);

  const handleClick = () => {
    if (totalCount > 0) {
      openNotifications();
      setShowEmpty(false);
    } else {
      setShowEmpty(!showEmpty);
    }
  };

  return (
    <div
      className={classes.container}
      onClick={handleClick}
      ref={emptyRef}
      title={totalCount > 0 ? `${totalCount} pending notification${totalCount !== 1 ? 's' : ''}` : 'No notifications'}
    >
      <i className={`tim-icons icon-bell-55 ${classes.icon} ${totalCount > 0 ? classes.iconActive : ''}`} />
      {totalCount > 0 && (
        <span className={classes.badge}>
          {totalCount > 99 ? '99+' : totalCount}
        </span>
      )}
      {showEmpty && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: '8px',
          minWidth: '200px',
          background: '#1e1e2f',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          zIndex: 1060,
          padding: '16px',
          textAlign: 'center',
        }}>
          <i className="tim-icons icon-bell-55" style={{ fontSize: '24px', color: 'rgba(255, 255, 255, 0.15)', display: 'block', marginBottom: '8px' }} />
          <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}>No notifications</div>
          <div style={{ color: 'rgba(255, 255, 255, 0.25)', fontSize: '11px', marginTop: '4px' }}>You're all caught up</div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
